import { useState, useEffect, useRef } from 'react';
import { productService, categoryService, inventoryService } from '../../services/api';
import dedupeByName from '../../utils/dedupeByName';
import { byCatalogueOrder, sortByCatalogueOrder } from '../../utils/catalogueOrder';

const useProductCatalog = (tenantConfig) => {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryObjects, setCategoryObjects] = useState({});
    const [categoryMap, setCategoryMap] = useState({});
    const [categoryIcons, setCategoryIcons] = useState({});
    const [printableCategories, setPrintableCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [bulkCalculatedStock, setBulkCalculatedStock] = useState({});
    const [productSearch, setProductSearch] = useState('');

    const loadCatalog = async () => {
        const [productData, rawCats] = await Promise.all([
            productService.getAllProducts(),
            categoryService.getAll().catch(() => []),
        ]);
        const cats = dedupeByName(rawCats);

        setProducts(productData);

        if (cats.length > 0) {
            // sort_order first (the portal's arrangement finally reaches the
            // till), name as the tie-break.
            setCategories(sortByCatalogueOrder(cats).map(c => c.name));
            const objMap = {};
            cats.forEach(c => { objMap[c.name] = c; });
            setCategoryObjects(objMap);
            const map = {};
            cats.forEach(c => { map[c.name] = c.id; });
            setCategoryMap(map);
            const iconMap = {};
            cats.forEach(c => { if (c.icon) iconMap[c.name] = c.icon; });
            setCategoryIcons(iconMap);
            setPrintableCategories(cats.filter(c => c.is_kitchen_display).map(c => c.name));
        }
        // Categories now always come from the categories API, no config fallback

        const autoProducts = productData.filter(p => p.stock_calculation_type === 'auto');
        if (autoProducts.length > 0) {
            try {
                const codes = autoProducts.map(p => p.product_code);
                const results = await inventoryService.calculateBulkProductStock(codes);
                setBulkCalculatedStock(results);
            } catch (err) {
                console.error('Error calculating bulk stock on load:', err);
            }
        }

        return { productData };
    };

    const refreshBulkStock = async () => {
        const autoProducts = products.filter(p => p.stock_calculation_type === 'auto');
        if (autoProducts.length === 0) return;
        try {
            const codes = autoProducts.map(p => p.product_code);
            const results = await inventoryService.calculateBulkProductStock(codes);
            setBulkCalculatedStock(results);
        } catch (err) {
            console.error('Error refreshing stock calculations:', err);
        }
    };

    // Stock can move without this surface doing anything: a refund taken in
    // the Transactions tab puts returned ingredients back, and the tiles
    // would keep showing the pre-refund figure until someone reloaded the
    // page. Following the existing `orderEvent`/`orderSubmitted` pattern
    // rather than threading a callback through two different shells — the
    // native station and the legacy till mount the refund UI in different
    // places, and neither owns this hook.
    //
    // The ref keeps the listener stable: refreshBulkStock is redefined on
    // every render, so depending on it directly would tear down and re-add
    // the listener continuously.
    const refreshBulkStockRef = useRef(refreshBulkStock);
    refreshBulkStockRef.current = refreshBulkStock;
    useEffect(() => {
        const onStockChanged = () => { refreshBulkStockRef.current(); };
        window.addEventListener('stockChanged', onStockChanged);
        return () => window.removeEventListener('stockChanged', onStockChanged);
    }, []);

    // Filter products when category/subcategory/search changes
    useEffect(() => {
        const filterProducts = async () => {
            if (!selectedCategory) {
                setFilteredProducts([]);
                setSubcategories([]);
                setSelectedSubcategory(null);
                return;
            }

            try {
                const catId = categoryMap[selectedCategory];
                let formattedSubcats = [];
                if (catId) {
                    const subs = await categoryService.getSubcategories(catId);
                    if (Array.isArray(subs)) {
                        formattedSubcats = subs.map(s => ({
                            id: s.id,
                            name: s.name,
                            translation_mode: s.translation_mode,
                            manual_translation: s.manual_translation,
                        }));
                    }
                }
                setSubcategories(sortByCatalogueOrder(formattedSubcats));

                if (selectedSubcategory && !formattedSubcats.some(sub => sub.name === selectedSubcategory)) {
                    setSelectedSubcategory(null);
                }

                let filtered;
                if (selectedSubcategory) {
                    filtered = products.filter(
                        p => p.category === selectedCategory && p.subcategory === selectedSubcategory
                    ).sort(byCatalogueOrder);
                } else {
                    filtered = products.filter(p => p.category === selectedCategory)
                        .sort(byCatalogueOrder);
                }

                if (productSearch.trim()) {
                    const term = productSearch.toLowerCase().trim();
                    filtered = filtered.filter(p =>
                        p.name.toLowerCase().includes(term) ||
                        p.product_code.toLowerCase().includes(term) ||
                        (p.subcategory && p.subcategory.toLowerCase().includes(term))
                    );
                }

                setFilteredProducts(filtered);
            } catch (err) {
                console.error('Failed to load subcategories:', err);
                setSubcategories([]);
                let filtered = products.filter(p => p.category === selectedCategory)
                    .sort(byCatalogueOrder);
                if (productSearch.trim()) {
                    const term = productSearch.toLowerCase().trim();
                    filtered = filtered.filter(p =>
                        p.name.toLowerCase().includes(term) ||
                        p.product_code.toLowerCase().includes(term) ||
                        (p.subcategory && p.subcategory.toLowerCase().includes(term))
                    );
                }
                setFilteredProducts(filtered);
            }
        };

        filterProducts();
    }, [selectedCategory, selectedSubcategory, products, productSearch, categoryMap]);

    return {
        products, setProducts,
        filteredProducts,
        categories,
        categoryObjects,
        categoryMap,
        categoryIcons,
        printableCategories,
        selectedCategory, setSelectedCategory,
        subcategories,
        selectedSubcategory, setSelectedSubcategory,
        bulkCalculatedStock, setBulkCalculatedStock,
        productSearch, setProductSearch,
        loadCatalog,
        refreshBulkStock,
    };
};

export default useProductCatalog;
